import React, { Fragment, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { translate } from "react-i18next"
import compose from "ramda/src/compose"
import { connect } from "react-redux"
import _find from "lodash/find"
import _filter from "lodash/filter"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import _map from "lodash/map"
import moment from "moment"

import DefaultTitle from "shared/components/DefaultTitle"
import Manager from "./components/Manager"
import NoSchedule from "./components/NoSchedule"
import Schedule from "./components/Schedule"
import History from "./components/History"
import Wizard from "../../components/Wizard"
import WizardStep from "../../components/Wizard/WizardStep"
import StepModals from "./components/StepModals"
import WizardSwitch from "components/Wizard/WizardSwitch"
import InterruptModal from "./components/InterruptScheduleModal/index"
import CancelModal from "./components/CancelScheduleModal/index"
import { showToast } from "../../components/Toast"
import Loading from "../../components/Loading"

import { maskTel } from "services/utils/mask"
import { formatServiceRequestData, payloadSchedule } from "./helpers"
import {
  getSVAs,
  getServiceRequests,
  getRemainingHours,
  findAllAssets,
  saveOperation,
  saveServiceRequest
} from "services/state/actions"

moment.locale("pt-br")

const InterfaceOnline = ({
  t,
  customer,
  getSVAs,
  findAllAssets,
  getServiceRequests,
  getRemainingHours,
  saveOperation,
  saveServiceRequest
}) => {
  const [svaAsset, setSvaAsset] = useState([])
  const [productServiceRequest, setProductServiceRequest] = useState([])
  const [remainingHours, setRemainingHours] = useState(0)
  const [numVinculado, setNumVinculado] = useState("")
  const [isLoadingServiceRequest, setIsLoadingServiceRequest] = useState(false)
  const [schedules, setSchedules] = useState({})
  const [newSchedulingData, setNewSchedulingData] = useState(payloadSchedule)
  const [svaSelected, setSvaSelected] = useState({})
  const [isOpenInterruptSchedule, setIsOpenInterruptSchedule] = useState(false)
  const [isOpenCancelSchedule, setIsOpenCancelSchedule] = useState(false)
  const [isOpenConfirm, setIsOpenConfirm] = useState(false)
  const [isOpenSchedule, setIsOpenSchedule] = useState(false)
  const [isLoadingSaveSchedule, setIsLoadingSaveSchedule] = useState(false)

  useEffect(() => {
    if (customer && customer.customerId) {
      findSvaList()
    }
  }, [])

  const findSvaList = async () => {
    setIsLoadingServiceRequest(true)
    try {
      const response = await getSVAs(customer.customerId)

      if (!response.payload) return

      const assetList = response.payload.data.assetList
      const svaAsset = _filter(
        assetList,
        (item) =>
          item.assetProductId === 125155 || item.assetProductId === 125456
      )
      if (!_isEmpty(svaAsset)) {
        setSvaAsset(svaAsset)
        setSvaSelected((prevSelected) => {
          const selected =
            _find(svaAsset, (item) => item.assetId === prevSelected.assetId) ||
            svaAsset[0]
          return selected
        })
        await findNum(svaAsset[0].assetId)
        await findServiceRequest(customer, svaAsset[0])
        await findRemainingHours(svaAsset[0].assetId)
      }
      setIsLoadingServiceRequest(false)
    } catch (error) {
      console.error("[Error fetching SVA data]", error)
      setIsLoadingServiceRequest(false)
    }
  }

  const findNum = async (assetId) => {
    try {
      const response = await findAllAssets(assetId)
      const attributes = _get(
        response,
        "payload.data.asset.attributes.attribute",
        []
      )
      const numVinculadoAttr = _find(attributes, {
        acronym: "NUM_VINCULADO",
        status: "ATIVO"
      })
      const numVinculado = numVinculadoAttr
        ? numVinculadoAttr.value.replace(/^0+/, "")
        : ""
      setNumVinculado(maskTel(numVinculado))
    } catch (error) {
      console.error("[Error fetching numVinculado]", error)
    }
  }

  const findServiceRequest = async (customer, svaAsset) => {
    try {
      const response = await getServiceRequests(
        customer.customerId,
        svaAsset.assetId
      )

      if (response && response.payload && response.payload.data) {
        setProductServiceRequest(response.payload.data.objectList)
        findSchedule(svaAsset, response.payload.data.objectList)
      }
    } catch (error) {
      console.error("[Error fetching Service Request data]", error)
    }
  }

  const findSchedule = (svaAsset, productService) => {
    let schedule = { originPhoneNumber: svaAsset.assetTag }

    if (
      productService &&
      productService.length > 0 &&
      productService[0] &&
      productService[0].fields
    ) {
      const findOperationType = _find(
        productService[0].fields,
        (item) =>
          item.fieldName === "OPERATION_TYPE" &&
          item.stringFieldValue !== "CANCELAR"
      )
      if (!_isEmpty(findOperationType)) {
        const findStartDate = _find(
          productService[0].fields,
          (item) =>
            item.fieldName === "SCHEDULED_START_DATE" ||
            item.fieldName === "START_DATE"
        )
        const startDateFieldValue =
          findStartDate.dateFieldValue || findStartDate.stringFieldValue
        const validStartDate = moment(startDateFieldValue).isValid()
        const startDate = validStartDate ? startDateFieldValue : null

        const findEndDate = _find(
          productService[0].fields,
          (item) =>
            item.fieldName === "SCHEDULED_END_DATE" ||
            item.fieldName === "END_DATE"
        )
        const endDateFieldValue =
          findEndDate.dateFieldValue || findEndDate.stringFieldValue
        const validEndDate = moment().isBefore(moment(endDateFieldValue))
        const endDate = validEndDate ? endDateFieldValue : null

        const targetPhoneList =
          _find(
            productService[0].fields,
            (item) =>
              item.fieldName === "SCHEDULED_TARGET_PHONE_LIST" ||
              item.fieldName === "TARGET_PHONE_LIST"
          ) || []

        if (
          !_isEmpty(startDate) &&
          !_isEmpty(endDate) &&
          !_isEmpty(targetPhoneList.stringFieldValue)
        ) {
          schedule = {
            ...schedule,
            startDate: moment(startDate).format("YYYY-MM-DD HH:mm:ssZZ"),
            endDate: moment(endDate).format("YYYY-MM-DD HH:mm:ssZZ"),
            targetPhoneList: JSON.parse(targetPhoneList.stringFieldValue)
          }
        }
      }
    }

    setSchedules((prevSchedules) => ({
      ...prevSchedules,
      [svaAsset.assetId]: schedule
    }))
  }

  const findRemainingHours = async (assetId) => {
    try {
      const response = await getRemainingHours(customer.customerId, assetId)
      const remainingHours =
        response &&
        response.payload &&
        response.payload.data &&
        response.payload.data.remainingHours
      setRemainingHours(remainingHours)
    } catch (error) {
      console.error("[Error fetching remaining hours]", error)
    }
  }

  const changeDDG = async (e) => {
    const assetId = parseInt(e.target.value, 0)
    const selected = svaAsset.find((item) => item.assetId === assetId)
    if (selected) {
      setIsLoadingServiceRequest(true)
      setSvaSelected(selected)
      try {
        await findNum(assetId)
        await findServiceRequest(customer, selected)
        await findRemainingHours(assetId)
      } catch (error) {
        console.error("[Error changing DDG]", error)
      }
      setIsLoadingServiceRequest(false)
    }
    saveOperation(
      customer,
      "",
      "INTERFACE_ONLINE",
      "VIEW_HIST",
      "SUCCESS",
      "",
      "Interface online"
    )
  }

  const getNewSchedule = () => {
    const schedule = schedules[svaSelected.assetId]
    const schedulingData = {
      ...payloadSchedule,
      customerId: customer.customerId,
      documentNumber: customer.documentNumber,
      phoneNumber: svaSelected.assetTag,
      assetId: svaSelected.assetId,
      loadRedirect: {
        ...schedule,
        operationType: schedule.targetPhoneList ? "REAGENDAR" : "AGENDAR"
      }
    }

    setNewSchedulingData(schedulingData)
  }

  const handleGetOptions = () =>
    _map(svaAsset, (item) => ({
      value: item.assetId,
      label: item.assetTag
    }))

  const updateSchedulingData = (value, finished) => {
    setNewSchedulingData((prevState) => ({
      ...prevState,
      loadRedirect: value
    }))
    finished && saveSchedule(value)
  }

  const handleOpenNewSchedule = () => {
    getNewSchedule()
    setIsOpenSchedule(true)
  }

  const handleCloseNewSchedule = () => {
    setIsOpenSchedule(false)
  }

  const interruptSchedule = () => {
    const isInterrupt = true
    getNewSchedule()

    setNewSchedulingData((prevState) => {
      const newSchedule = {
        ...prevState,
        loadRedirect: {
          ...prevState.loadRedirect,
          operationType: "REAGENDAR",
          endDate: moment().add(5, "minutes").format("YYYY-MM-DD HH:mm:ssZZ")
        }
      }

      saveSchedule(newSchedule, isInterrupt)
      return newSchedule
    })

    setIsOpenInterruptSchedule(false)
  }

  const handleOpenInterruptSchedule = () => {
    setIsOpenInterruptSchedule(true)
    setIsOpenConfirm(true)
  }

  const cancelSchedule = () => {
    const isCancel = true
    getNewSchedule()

    setNewSchedulingData((prevState) => {
      const newSchedule = {
        ...prevState,
        loadRedirect: {
          ...prevState.loadRedirect,
          operationType: "CANCELAR"
        }
      }

      saveSchedule(newSchedule, isCancel)
      return newSchedule
    })

    setIsOpenCancelSchedule(false)
  }

  const handleOpenCancelSchedule = () => {
    setIsOpenCancelSchedule(true)
    setIsOpenConfirm(true)
  }

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false)
    setIsOpenCancelSchedule(false)
    setIsOpenInterruptSchedule(false)
  }

  const saveSchedule = (schedule, isInterrupt, isCancel) => {
    let payload = {}
    const targetPhoneList = _map(
      schedule.targetPhoneList || schedule.loadRedirect.targetPhoneList,
      (item) => {
        const phoneNumber = "0" + item.phoneNumber
        item.phoneNumber = phoneNumber
        return item
      }
    )
    if (!schedule.originPhoneNumber) {
      payload = {
        loadRedirect: {
          ...schedule,
          originPhoneNumber: svaSelected.assetTag
        }
      }
    }
    if (isInterrupt || isCancel) {
      payload = {
        ...payloadSchedule,
        ...newSchedulingData,
        customerId: customer.customerId,
        documentNumber: customer.documentNumber,
        phoneNumber: svaSelected.assetTag,
        assetId: svaSelected.assetId,
        loadRedirect: {
          ...schedule.loadRedirect,
          targetPhoneList: JSON.stringify(targetPhoneList)
        }
      }
    } else {
      payload = {
        ...payloadSchedule,
        ...newSchedulingData,
        loadRedirect: {
          ...schedule,
          targetPhoneList: JSON.stringify(targetPhoneList)
        }
      }
    }
    setIsLoadingSaveSchedule(true)
    saveServiceRequest(payload, callbackSaveServiceRequest)
  }

  const callbackSaveServiceRequest = (response, status) => {
    const error = status === "ERROR"
    setIsLoadingSaveSchedule(false)
    if (!error) {
      const assetId = svaSelected.assetId
      findNum(assetId)
      findServiceRequest(customer, svaSelected)
      findRemainingHours(assetId)
      return showToast({
        message: `Cliente, sua configuração foi concluída com sucesso! Número do protocolo: ${response.protocol}`,
        type: "sucess"
      })
    }

    return showToast({
      message:
        "Cliente, não foi possível prosseguir com sua solicitação. Tente novamente mais tarde ou entre em contato com o nosso atendimento.",
      type: "error"
    })
  }

  const formatHistoryData = formatServiceRequestData(productServiceRequest)

  return (
    <Fragment>
      <Loading isOpen={isLoadingSaveSchedule} />
      <div className="Grid-cell u-flex u-flexCol">
        <DefaultTitle>{t("DDG_MANAGER")}</DefaultTitle>

        <Manager
          t={t}
          remainingHours={remainingHours}
          getOptions={handleGetOptions}
          changeDDG={changeDDG}
        />

        {schedules[svaSelected.assetId] &&
        schedules[svaSelected.assetId].targetPhoneList ? (
          <Schedule
            t={t}
            schedule={schedules[svaSelected.assetId]}
            interruptIsOpen={handleOpenInterruptSchedule}
            cancelIsOpen={handleOpenCancelSchedule}
            onClickReschedule={handleOpenNewSchedule}
            loading={isLoadingServiceRequest}
          />
        ) : (
          <NoSchedule
            t={t}
            remainingHours={remainingHours}
            numVinculado={numVinculado}
            onClick={handleOpenNewSchedule}
            loading={isLoadingServiceRequest}
          />
        )}

        <History
          t={t}
          productServiceRequest={productServiceRequest}
          historyData={formatHistoryData}
          loading={isLoadingServiceRequest}
        />

        {isOpenSchedule && (
          <Wizard>
            <WizardStep
              key="numbers"
              component={StepModals}
              step="numbers"
              t={t}
              onClose={handleCloseNewSchedule}
              schedule={newSchedulingData}
              update={updateSchedulingData}
            />
            <WizardStep
              key="level"
              component={StepModals}
              step="level"
              t={t}
              onClose={handleCloseNewSchedule}
              schedule={newSchedulingData}
              update={updateSchedulingData}
            />
            <WizardStep
              key="schedule"
              component={StepModals}
              step="schedule"
              t={t}
              onClose={handleCloseNewSchedule}
              schedule={newSchedulingData}
              update={updateSchedulingData}
              customer={customer}
              saveOperation={saveOperation}
              hours={remainingHours}
            />
          </Wizard>
        )}

        {isOpenConfirm && (
          <Wizard onClose={handleCloseConfirm}>
            <WizardSwitch key="confirm">
              <WizardStep
                key="interrupt"
                test={() => isOpenInterruptSchedule}
                component={InterruptModal}
                isOpen={isOpenInterruptSchedule}
                onClose={() => setIsOpenInterruptSchedule(false)}
                onInterruptSchedule={interruptSchedule}
              />

              <WizardStep
                key="cancel"
                test={() => isOpenCancelSchedule}
                component={CancelModal}
                isOpen={isOpenCancelSchedule}
                onClose={() => setIsOpenCancelSchedule(false)}
                onCancelSchedule={cancelSchedule}
              />
            </WizardSwitch>
          </Wizard>
        )}
      </div>
    </Fragment>
  )
}

InterfaceOnline.propTypes = {
  t: PropTypes.func,
  customer: PropTypes.object,
  getSVAs: PropTypes.func,
  findAllAssets: PropTypes.func,
  getServiceRequests: PropTypes.func,
  getRemainingHours: PropTypes.func,
  saveOperation: PropTypes.func,
  saveServiceRequest: PropTypes.func
}

const mapStateToProps = ({ customer, svas }) => ({
  customer: customer.data,
  svas
})

const enhance = compose(
  translate(),
  connect(mapStateToProps, {
    getSVAs,
    getServiceRequests,
    getRemainingHours,
    findAllAssets,
    saveOperation,
    saveServiceRequest
  })
)
export default enhance(InterfaceOnline)



// WEBPACK FOOTER //
// ./src/scenes/NewInterfaceOnline/index.js